import React, { useEffect, useId, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useBrand } from '../../contexts/BrandContext';
import { useAnkerSprung } from '../../administration/ankerSprung';
import { Abschnitt, Bereich } from '../../pages/einstellungen/Bereich';
import { EinstLink } from '../../pages/einstellungen/EinstLink';
import { EinstellungsZeile } from '../../pages/einstellungen/EinstellungsZeile';
import { HilfeThemenListe } from '../../pages/einstellungen/Hilfe';
import { gruppeVon, HILFE_GRUPPEN_REIHE, HILFE_THEMEN, type HilfeThema } from '../../pages/einstellungen/hilfeThemen';
import { useEinstellungsGruppen } from '../../pages/einstellungen/useEinstellungsGruppen';
import { vergleichsForm } from '../SeitenRahmen';
import Ansprechperson, { useAnsprechpersonen } from './Ansprechperson';
import { hilfeDokumente, type BetreiberDokument } from './hilfeApi';
import { useHilfe } from './hilfeKontext';
import { anleitungenFiltern, dokumentAnker, hilfeAdresse, hilfeBereichGebaut, hilfeEintragSichtbar } from './hilfeLeiste';
import { gruppeVerwaltung, HILFE_TEXTE, keinTreffer } from './hilfeTexte';
import UeberClairis from './UeberClairis';

// Unterseite „Dokumente“ (BAU-HILFE.md 1.3, PF-308-1): die eingebauten Anleitungen wie im Pop-up „Hilfe“, durchsuchbar über
// Frage, Schritte und Hinweise, dazu die Wege zum Hilfe-Assistenten und zum Support und die Links zu Produkt und Recht. Lesestoff;
// seit F-24 mit „Neue Anfrage“ als Hauptaktion. Die eingebauten Anleitungen kommen aus dem Frontend; die Seite bleibt lesbar, auch wenn die Übersicht
// des Servers fehlt. PF-323 (BAU-HILFE.md 9.2 Punkt 3, 9.4): dahinter die eigenen Dokumente des Betreibers in derselben Form,
// Titel und Text statt Schritten und Hinweisen; ohne Weg (Fehler, 403 in einer Sicht) stehen nur die eingebauten.
// Profil-Nachbau F-23 (PF-NB-hilfe-1): Gruppen nach Aufgaben (gruppeVon), sichtbare Beschriftung der Suche, ohne Chat kein Versprechen des Assistenten.
// F-23 (PF-NB-recht-1): unten der Abschnitt „Über … und Recht“ (UeberClairis.tsx).
// F-24 (PF-TX-hilfe-1, SPEC-hilfe-b 3.1): kurze Unterzeile, „Neue Anfrage“ als Hauptaktion im Seitenkopf (nicht in einer Sicht),
// „Anleitungen und häufige Fragen“ nur für Bildschirmleser, „Selbst nicht weitergekommen?“ nur mit Ansprechperson oder Chat.
const T = HILFE_TEXTE.dokumente;

/** PF-323: die veröffentlichten eigenen Dokumente (GET /api/hilfe/dokumente), einmal je Seite, für diese Unterseite und die Suche
 *  der Leiste. Ohne Weg bleibt die Liste leer, ohne Fehlertext; fertig: geladen oder gescheitert. */
export function useEigeneDokumente(): { eigene: BetreiberDokument[]; fertig: boolean } {
  const [stand, setStand] = useState<{ eigene: BetreiberDokument[]; fertig: boolean }>({ eigene: [], fertig: false });
  useEffect(() => {
    const abbruch = new AbortController();
    hilfeDokumente(abbruch.signal)
      .then(daten => { if (!abbruch.signal.aborted) setStand({ eigene: daten.dokumente, fertig: true }); })
      .catch(() => { if (!abbruch.signal.aborted) setStand({ eigene: [], fertig: true }); });
    return () => abbruch.abort();
  }, []);
  return stand;
}

/** Text des Betreibers mit Absätzen (Dokumente, Neuigkeiten): Leerzeilen trennen Absätze, einfache Umbrüche bleiben im Absatz;
 *  nie als HTML oder Markdown. */
export function Absaetze({ text }: { text: string }) {
  return <>{text.split(/\n\s*\n/).map(absatz => absatz.trim()).filter(Boolean).map((absatz, index) => <p key={index} className="hilfe-absatz">{absatz}</p>)}</>;
}

/** Ein eigenes Dokument in der Form eines eingebauten Themas (pages/einstellungen/Hilfe.tsx, Thema): zugeklappt der Titel, offen
 *  der Text. Der Anker #hilfe-b-<id> öffnet es, auch bei einem späteren Wechsel des Ankers. F-23 (PF-NB-hilfe-1-nach): h3 unter
 *  dem Gruppentitel (h2) wie die Themen. */
export function EigenesDokument({ dokument }: { dokument: BetreiberDokument }) {
  const { hash, key } = useLocation();
  const anker = dokumentAnker(dokument.id);
  const amAnker = hash === `#${anker}`;
  const [offen, setOffen] = useState(amAnker);
  useEffect(() => { if (amAnker) setOffen(true); }, [amAnker, key]);
  const id = useId();
  return <section className="einst-hilfe-thema" id={anker} aria-labelledby={`${id}-frage`}>
    <h3 className="einst-hilfe-frage" id={`${id}-frage`}>
      <button type="button" className="einst-hilfe-knopf" aria-expanded={offen} aria-controls={`${id}-inhalt`} data-lesend
        onClick={() => setOffen(wert => !wert)}><ChevronRight size={16} aria-hidden="true" /><span>{dokument.titel}</span></button>
    </h3>
    <div className="einst-hilfe-inhalt" id={`${id}-inhalt`} hidden={!offen}><Absaetze text={dokument.text} /></div>
  </section>;
}

/** Die Themen, die dieses Konto sieht: Firmenthemen nur, wenn das Konto die Einstellungen der Firma hat und der Bereich in
 *  deren Navigation steht, auch in einer Sicht. bereit: die Firmenbereiche sind geladen. Die Seite
 *  ruft das einmal auf, für die Suche der Leiste und für diese Unterseite. */
export function useHilfeThemen(): { themen: HilfeThema[]; bereit: boolean } {
  const { gruppen, bereicheLaden } = useEinstellungsGruppen();
  const firma = gruppen.find(gruppe => gruppe.key === 'firma');
  return { themen: HILFE_THEMEN.filter(thema => !thema.bereich || !!firma?.eintraege.some(eintrag => eintrag.key === thema.bereich)), bereit: !bereicheLaden };
}

export default function Dokumente({ themen, bereit, eigene = [], eigeneFertig = true }: { themen: HilfeThema[]; bereit: boolean;
  eigene?: BetreiberDokument[]; eigeneFertig?: boolean }) {
  const brand = useBrand();
  const { uebersicht, sicht } = useHilfe();
  const { hash } = useLocation();
  const personen = useAnsprechpersonen();
  const zugangFragen = !themen.some(thema => thema.bereich);
  // Ein Anker #hilfe-<id> rückt sein Thema in den Blick, sobald auch die Firmenthemen stehen; #hilfe-b-<id>, sobald die eigenen
  // Dokumente geladen sind.
  useAnkerSprung(bereit && (eigeneFertig || !hash.startsWith(`#${dokumentAnker('')}`)));

  const [text, setText] = useState('');
  const muster = vergleichsForm(text.trim());
  const { themen: gezeigt, eigene: eigeneGezeigt } = anleitungenFiltern(themen, eigene, text);
  const keine = !!muster && gezeigt.length === 0 && eigeneGezeigt.length === 0;
  const chat = hilfeEintragSichtbar('chat', { uebersicht, organisation: brand.tenantLabel });
  const sucheId = useId();
  // F-23 (PF-NB-hilfe-1, Z-04): Gruppen in fester Reihenfolge, leere entfallen (auch bei einer Suche). PF-NB-hilfe-1-nach: Gruppentitel
  // h2, die Themen darunter h3 (keine Ebene übersprungen).
  const gruppen = HILFE_GRUPPEN_REIHE.map(gruppe => ({ gruppe, themen: gezeigt.filter(thema => gruppeVon(thema) === gruppe) }))
    .filter(eintrag => eintrag.themen.length > 0);
  const gruppenTitel = (gruppe: (typeof HILFE_GRUPPEN_REIHE)[number]) => (gruppe === 'verwaltung' ? gruppeVerwaltung(brand.tenantLabel) : T.gruppen[gruppe]);
  // Escape mit Text leert das Feld und führt nicht „Zurück zum Chat“ (die Seite hört Escape am Dokument).
  const tasten = (event: React.KeyboardEvent<HTMLInputElement>) => { if (event.key === 'Escape' && text) { event.preventDefault(); setText(''); } };
  const neueAnfrage = hilfeBereichGebaut('tickets') && !sicht ? <EinstLink className="adm-button adm-primary" to={hilfeAdresse('tickets', 'neu')}>{T.neueAnfrage}</EinstLink> : undefined;

  return <Bereich titel={T.titel} beschreibung={T.beschreibung} aktion={neueAnfrage}>
    {/* F-23 (H-17): sichtbare Beschriftung über dem Feld statt nur Platzhalter. */}
    <label className="hilfe-suche-label" htmlFor={sucheId}>{T.sucheName}</label>
    <div className="hilfe-suche" role="search" aria-label={T.sucheName} data-lesend>
      <Search size={16} aria-hidden="true" />
      <input id={sucheId} type="search" placeholder={T.suchePlatzhalter} value={text} autoComplete="off" spellCheck={false}
        onChange={event => setText(event.target.value)} onKeyDown={tasten} />
    </div>
    <Abschnitt titel={T.anleitungen} titelVersteckt>
      <p className="hilfe-leer" role="status">{keine && <>
        <span>{keinTreffer(text.trim())}</span>
        {chat && <EinstLink className="adm-text-link hilfe-leer-weg" to={hilfeAdresse('chat')}>{T.imChatFragen}</EinstLink>}
        {!chat && hilfeBereichGebaut('tickets') && <EinstLink className="adm-text-link hilfe-leer-weg" to={hilfeAdresse('tickets', 'neu')}>{T.neueAnfrage}</EinstLink>}
      </>}</p>
      {!keine && gruppen.map(({ gruppe, themen: liste }) => <section key={gruppe} className="hilfe-gruppe" aria-labelledby={`${sucheId}-${gruppe}`}>
        <h2 className="hilfe-gruppe-titel" id={`${sucheId}-${gruppe}`}>{gruppenTitel(gruppe)}</h2>
        <HilfeThemenListe themen={liste} zugangFragen={zugangFragen} ebene={3} />
      </section>)}
      {!keine && eigeneGezeigt.length > 0 && <section className="hilfe-gruppe" aria-labelledby={`${sucheId}-eigene`}>
        <h2 className="hilfe-gruppe-titel" id={`${sucheId}-eigene`}>{T.gruppen.eigene}</h2>
        {eigeneGezeigt.map(dokument => <EigenesDokument key={dokument.id} dokument={dokument} />)}
      </section>}
    </Abschnitt>
    {(personen.length > 0 || chat) && <Abschnitt titel={T.weiter}>
      {/* PF-321-2: zuerst die Ansprechperson für KI der Firma (BAU-HILFE 9.2 Punkt 4); ohne Kontakt keine Zeile. */}
      <Ansprechperson personen={personen} />
      {chat && <EinstellungsZeile ebene={3} titel={T.assistent} erklaerung={T.assistentErklaerung}>
        <EinstLink className="adm-button" to={hilfeAdresse('chat')}>{T.chatOeffnen}</EinstLink>
      </EinstellungsZeile>}
      {/* F-24 (SPEC-hilfe-b 3.1): der Weg zum Support steht als „Neue Anfrage“ im Seitenkopf. */}
    </Abschnitt>}
    <UeberClairis />
  </Bereich>;
}
